import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Calculator,
  FloppyDisk,
  ArrowsClockwise,
  PencilSimple,
  FilePdf,
  DownloadSimple,
  Paperclip,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const COST_FIELDS = [
  { key: "box_pc", label: "Box PC", code: "boxPC", unit: "₹" },
  { key: "pouch_pc", label: "Pouch PC", code: "pouchPC", unit: "₹" },
  { key: "lc", label: "Logistics", code: "LC", unit: "₹" },
  { key: "oc", label: "Operational", code: "OC", unit: "₹" },
  { key: "pm", label: "Profit Margin", code: "PM", unit: "%" },
  { key: "gst", label: "GST", code: "GST", unit: "%" },
];

function fmt(n, digits = 2) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function PackingChip({ type }) {
  const palette =
    {
      Tub: "bg-blue-50 text-blue-700 border-blue-200",
      Pouch: "bg-emerald-50 text-emerald-700 border-emerald-200",
      HoReCa: "bg-amber-50 text-amber-700 border-amber-200",
    }[type] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${palette}`}
    >
      {type}
    </span>
  );
}

function CostBlockEditor({ title, accent, value, onChange }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-2 h-6 rounded-sm ${accent}`} />
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {COST_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">
              {f.label} <span className="text-slate-400">· {f.code}</span>
            </Label>
            <div className="mt-1 relative">
              <Input
                type="number"
                step={f.key === "pm" || f.key === "gst" ? "0.5" : "1"}
                value={value?.[f.key] ?? ""}
                onChange={(e) =>
                  onChange({
                    ...value,
                    [f.key]:
                      e.target.value === "" ? 0 : parseFloat(e.target.value),
                  })
                }
                className="h-9 pr-9 text-sm"
                data-testid={`var-${title.toLowerCase()}-${f.key}`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400">
                {f.unit}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditRowDialog({ open, row, globalRr, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {

      // eslint-disable-next-line
      setForm({
        rm_cost: row.rm_cost ?? "",
        packing_type: row.packing_type || "Tub",
        rehydration_ratio:
          row.rehydration_ratio_override !== null &&
          row.rehydration_ratio_override !== undefined
            ? row.rehydration_ratio_override
            : "",
        mrp: row.manual_mrp ?? "",
        net_weight_g: row.net_weight_g ?? "",
        rehydrated_weight_g: row.rehydrated_weight_g ?? "",
      });
    }
  }, [row]);

  const submit = async () => {
    if (!row) return;
    setSaving(true);
    try {
      const payload = {
        rm_cost: parseFloat(form.rm_cost) || 0,
        packing_type: form.packing_type,
        rehydration_ratio:
          form.rehydration_ratio === "" || form.rehydration_ratio === null
            ? null
            : parseFloat(form.rehydration_ratio),
        mrp: parseFloat(form.mrp) || 0,
        net_weight_g: parseFloat(form.net_weight_g) || 0,
        rehydrated_weight_g: parseFloat(form.rehydrated_weight_g) || 0,
      };
      await api.put(`/products/${row.id}`, payload);
      toast.success(`${row.name} updated`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="edit-pricing-dialog">
        <DialogHeader>
          <DialogTitle>Edit Pricing · {row.name}</DialogTitle>
          <DialogDescription>
            Adjust RM cost, packing, rehydration ratio, and manual MRP for this product.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                RM Cost (₹)
              </Label>
              <Input
                type="number"
                step="0.01"
                value={form.rm_cost}
                onChange={(e) =>
                  setForm({ ...form, rm_cost: e.target.value })
                }
                className="mt-1.5"
                data-testid="edit-rm-cost"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Packing Type
              </Label>
              <select
                value={form.packing_type}
                onChange={(e) =>
                  setForm({ ...form, packing_type: e.target.value })
                }
                className="mt-1.5 h-10 w-full px-3 border border-slate-200 rounded-md bg-white text-sm"
                data-testid="edit-packing-type"
              >
                <option value="Tub">Tub (510 ml)</option>
                <option value="Pouch">Pouch (50 g)</option>
                <option value="HoReCa">HoReCa (250 g)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Net Weight (g)
              </Label>
              <Input
                type="number"
                value={form.net_weight_g}
                onChange={(e) =>
                  setForm({ ...form, net_weight_g: e.target.value })
                }
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Rehydrated Weight (g)
              </Label>
              <Input
                type="number"
                value={form.rehydrated_weight_g}
                onChange={(e) =>
                  setForm({ ...form, rehydrated_weight_g: e.target.value })
                }
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Rehydration Ratio
              </Label>
              <Input
                type="number"
                step="0.1"
                value={form.rehydration_ratio}
                placeholder={`global: ${globalRr}`}
                onChange={(e) =>
                  setForm({ ...form, rehydration_ratio: e.target.value })
                }
                className="mt-1.5"
                data-testid="edit-rr"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Blank → use global ({globalRr})
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Manual MRP (₹, incl. GST)
              </Label>
              <Input
                type="number"
                step="0.01"
                value={form.mrp}
                onChange={(e) => setForm({ ...form, mrp: e.target.value })}
                className="mt-1.5"
                data-testid="edit-mrp"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-3 h-9 rounded-md border border-slate-200 bg-white text-slate-700 text-sm"
            data-testid="edit-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 h-9 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            data-testid="edit-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SnapshotDialog({ open, onClose, onAttached }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/customers", {
          params: { status: "all", owner: "all", sort: "updated_at" },
        });

        setCustomers(data || []);
      } catch (e) {
        // Non-fatal: the modal remains usable without the customer list.
        // Show a friendly toast + surface the error in the console for debugging.
        console.warn("Pricing snapshot: customer list fetch failed", e);
        toast.error(e?.response?.data?.detail || "Couldn't load the customer list");
      }
    })();
  }, [open]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post(
        "/pricing/snapshot",
        {},
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pricing-snapshot-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Snapshot downloaded");
      onClose();
    } catch {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };

  const attach = async () => {
    if (!customerId) {
      toast.error("Pick a customer first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/pricing/snapshot", {
        customer_id: customerId,
      });
      toast.success(`Attached to ${data.customer_name}`);
      onAttached?.(customerId);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Attach failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="snapshot-dialog">
        <DialogHeader>
          <DialogTitle>Pricing Snapshot</DialogTitle>
          <DialogDescription>
            Generate a PDF of the current pricing table (B2C + B2B).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Generate a PDF of the current pricing table (B2C + B2B). Either
            download it directly, or attach it to a customer&apos;s record.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Attach to customer (optional)
            </Label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="mt-1.5 h-10 w-full px-3 border border-slate-200 rounded-md bg-white text-sm"
              data-testid="snapshot-customer-select"
            >
              <option value="">— Select customer —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.company ? `(${c.company})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter className="flex-row justify-between gap-2">
          <button
            onClick={download}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            data-testid="snapshot-download"
          >
            <DownloadSimple size={14} /> Download
          </button>
          <button
            onClick={attach}
            disabled={busy || !customerId}
            className="inline-flex items-center gap-2 px-4 h-9 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            data-testid="snapshot-attach"
          >
            <Paperclip size={14} /> Attach & Download
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Pricing() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [targetRehydrated, setTargetRehydrated] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [snapshotOpen, setSnapshotOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      const num = parseFloat(targetRehydrated);
      if (Number.isFinite(num) && num > 0) params.target_rehydrated = num;
      const [s, p] = await Promise.all([
        api.get("/pricing/settings"),
        api.get("/pricing/preview", { params }),
      ]);
      setSettings(s.data);
      setDraft(s.data);
      setRows(p.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load pricing");
    } finally {
      setLoading(false);
    }
  }, [targetRehydrated]);

  useEffect(() => {
    // eslint-disable-next-line
    load();
  }, [load]);

  const saveSettings = async () => {
    if (!draft) return;
    setSavingSettings(true);
    try {
      const { data } = await api.put("/pricing/settings", draft);
      setSettings(data);
      setDraft(data);
      toast.success("Pricing variables updated");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingSettings(false);
    }
  };

  const dirty = useMemo(() => {
    if (!settings || !draft) return false;
    return JSON.stringify(settings) !== JSON.stringify(draft);
  }, [settings, draft]);

  const isReverse = useMemo(() => {
    const num = parseFloat(targetRehydrated);
    return Number.isFinite(num) && num > 0;
  }, [targetRehydrated]);

  return (
    <div data-testid="pricing-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Sales · Pricing
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Product Pricing
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Derive B2C &amp; B2B pricing. Edit per-product overrides and share
              snapshots with customers.
            </p>
          </div>
          <button
            onClick={() => setSnapshotOpen(true)}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
            data-testid="open-snapshot-btn"
          >
            <FilePdf size={16} weight="duotone" /> Snapshot PDF
          </button>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* B2C / B2B Cost Blocks side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CostBlockEditor
            title="B2C"
            accent="bg-blue-500"
            value={draft?.b2c}
            onChange={(v) => setDraft((d) => ({ ...d, b2c: v }))}
          />
          <CostBlockEditor
            title="B2B"
            accent="bg-amber-500"
            value={draft?.b2b}
            onChange={(v) => setDraft((d) => ({ ...d, b2b: v }))}
          />
        </div>

        {/* Shared controls: Global RR + Reverse calc + Save */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-end gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center">
              <Calculator size={16} weight="duotone" />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Global
              </div>
              <div className="font-heading text-base font-semibold text-slate-900">
                Shared inputs
              </div>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">
              Rehydration Ratio (default)
            </Label>
            <Input
              type="number"
              step="0.1"
              value={draft?.rr ?? ""}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  rr: e.target.value === "" ? 0 : parseFloat(e.target.value),
                }))
              }
              className="mt-1 h-9 w-28"
              data-testid="var-global-rr"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">
              Target Rehydrated (g) — reverse
            </Label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                type="number"
                value={targetRehydrated}
                onChange={(e) => setTargetRehydrated(e.target.value)}
                placeholder="e.g. 270"
                className="h-9 w-32"
                data-testid="reverse-target-input"
              />
              {isReverse && (
                <button
                  onClick={() => setTargetRehydrated("")}
                  className="h-9 px-2 rounded-md border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50"
                  data-testid="reverse-clear-btn"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="flex-1" />
          {dirty && (
            <button
              onClick={() => setDraft(settings)}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50"
              data-testid="reset-variables-btn"
            >
              <ArrowsClockwise size={12} weight="bold" /> Discard
            </button>
          )}
          <button
            onClick={saveSettings}
            disabled={!dirty || savingSettings}
            className="inline-flex items-center gap-2 px-4 h-9 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="save-variables-btn"
          >
            <FloppyDisk size={14} weight="bold" />
            {savingSettings ? "Saving…" : "Save & recompute"}
          </button>
        </div>

        {/* Price table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Computed
              </div>
              <h2 className="font-heading text-lg font-semibold text-slate-900">
                {isReverse
                  ? `Price Table @ ${targetRehydrated} g rehydrated`
                  : "Price Table"}
              </h2>
            </div>
            <div className="text-[11px] text-slate-500">
              <code className="font-mono">TC = RM + packPC + LC + OC</code> ·{" "}
              <code className="font-mono">P = TC × PM</code> ·{" "}
              <code className="font-mono">Final = (TC + P) × (1 + GST)</code>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pricing-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  <th className="px-3 py-3">Product</th>
                  <th className="px-2 py-3">Pack</th>
                  <th className="px-2 py-3 text-right">Net g</th>
                  <th className="px-2 py-3 text-right">Rehy g</th>
                  <th className="px-2 py-3 text-right">RR</th>
                  <th className="px-2 py-3 text-right">RM ₹</th>
                  <th className="px-2 py-3 text-right bg-blue-50/60">
                    B2C TC
                  </th>
                  <th className="px-2 py-3 text-right bg-blue-50/60">
                    B2C Profit
                  </th>
                  <th className="px-2 py-3 text-right bg-blue-50/60">
                    B2C Final
                  </th>
                  <th className="px-2 py-3 text-right bg-amber-50/60">
                    B2B TC
                  </th>
                  <th className="px-2 py-3 text-right bg-amber-50/60">
                    B2B Profit
                  </th>
                  <th className="px-2 py-3 text-right bg-amber-50/60">
                    B2B Final
                  </th>
                  <th className="px-2 py-3 text-right">Manual MRP</th>
                  <th className="px-2 py-3 text-right">Eff. Profit</th>
                  <th className="px-2 py-3 text-center">Edit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td
                      colSpan={15}
                      className="px-6 py-10 text-center text-slate-500"
                    >
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={15}
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      No active products yet.
                    </td>
                  </tr>
                )}
                {!loading &&
                  rows.map((r) => {
                    const b2cFinal = isReverse
                      ? r.reverse_b2c_final
                      : r.b2c?.final_price;
                    const b2bFinal = isReverse
                      ? r.reverse_b2b_final
                      : r.b2b?.final_price;
                    return (
                      <tr
                        key={r.id}
                        className="hover:bg-slate-50"
                        data-testid={`pricing-row-${r.id}`}
                      >
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-slate-900">
                            {r.name}
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono">
                            {r.product_id}
                          </div>
                        </td>
                        <td className="px-2 py-2.5">
                          <PackingChip type={r.packing_type} />
                        </td>
                        <td className="px-2 py-2.5 text-right text-slate-700">
                          {fmt(r.net_weight_g, 0)}
                        </td>
                        <td className="px-2 py-2.5 text-right text-slate-700">
                          {fmt(r.rehydrated_weight_g, 0)}
                        </td>
                        <td className="px-2 py-2.5 text-right text-slate-700">
                          {fmt(r.rehydration_ratio, 2)}
                          {r.rehydration_ratio_override !== null &&
                            r.rehydration_ratio_override !== undefined && (
                              <span className="ml-1 text-[9px] text-amber-600 font-semibold">
                                ⁕
                              </span>
                            )}
                        </td>
                        <td className="px-2 py-2.5 text-right text-slate-700">
                          {fmt(r.rm_cost)}
                        </td>
                        <td className="px-2 py-2.5 text-right text-slate-700 bg-blue-50/30">
                          {fmt(r.b2c?.total_cost)}
                        </td>
                        <td className="px-2 py-2.5 text-right text-slate-700 bg-blue-50/30">
                          {fmt(r.b2c?.profit)}
                        </td>
                        <td className="px-2 py-2.5 text-right font-semibold text-slate-900 bg-blue-50/30">
                          ₹ {fmt(b2cFinal)}
                        </td>
                        <td className="px-2 py-2.5 text-right text-slate-700 bg-amber-50/30">
                          {fmt(r.b2b?.total_cost)}
                        </td>
                        <td className="px-2 py-2.5 text-right text-slate-700 bg-amber-50/30">
                          {fmt(r.b2b?.profit)}
                        </td>
                        <td className="px-2 py-2.5 text-right font-semibold text-slate-900 bg-amber-50/30">
                          ₹ {fmt(b2bFinal)}
                        </td>
                        <td className="px-2 py-2.5 text-right text-slate-900 font-medium">
                          {r.manual_mrp ? `₹ ${fmt(r.manual_mrp)}` : "—"}
                        </td>
                        <td className="px-2 py-2.5 text-right text-xs">
                          {r.effective_profit_pct === null ||
                          r.effective_profit_pct === undefined ? (
                            <span className="text-slate-400">—</span>
                          ) : (
                            <div
                              className={
                                r.effective_profit_pct < 0
                                  ? "text-red-600 font-semibold"
                                  : r.effective_profit_pct < (settings?.b2c?.pm || 0)
                                  ? "text-amber-600 font-semibold"
                                  : "text-emerald-700 font-semibold"
                              }
                              data-testid={`eff-profit-${r.id}`}
                            >
                              {fmt(r.effective_profit_pct, 1)}%
                              <div className="text-[10px] font-normal text-slate-500">
                                ₹ {fmt(r.effective_profit)}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          <button
                            onClick={() => setEditingRow(r)}
                            className="p-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-blue-50 hover:text-blue-600"
                            data-testid={`edit-row-${r.id}`}
                            title="Edit RM cost, packing, RR, manual MRP"
                          >
                            <PencilSimple size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 flex items-center gap-2">
            <span className="text-amber-600 font-semibold">⁕</span> indicates a
            per-product Rehydration Ratio override (vs. global {settings?.rr})
          </div>
        </div>
      </div>

      <EditRowDialog
        open={!!editingRow}
        row={editingRow}
        globalRr={settings?.rr}
        onClose={() => setEditingRow(null)}
        onSaved={load}
      />
      <SnapshotDialog
        open={snapshotOpen}
        onClose={() => setSnapshotOpen(false)}
      />
    </div>
  );
}
